import { AddResidentAllergiesForm } from "app/services/api/types/residents/models/AddResidentAllergiesForm";
import { useForm } from "react-hook-form";
import { useResidentContext } from "../provider/ResidentProvider";
import {
  useDeleteResidentAllergiesMediaMutation,
  useUpdateResidentAllergiesMutation,
} from "app/services/api/residentsApi";
import { useState } from "react";
import { handleBackendError } from "ui/utils/Errors";
import { DocumentPickerAsset } from "expo-document-picker";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { usePageFocus } from "ui/utils/FocusHook";
import { isDocumentPickerAsset, isMediaModel } from "ui/types/ThrelsFileType";

export const useHandleResidentAllergies = () => {
  const { residentId, resident } = useResidentContext();
  const [triggerUpdateResidentAllergies] = useUpdateResidentAllergiesMutation();
  const [triggerMediaDelete] = useDeleteResidentAllergiesMediaMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { showNewToast, showErrorToast } = useDisplayToast();

  const {
    control,
    reset,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = useForm<AddResidentAllergiesForm>({
    defaultValues: {
      allergies: "",
      allergies_media: [],
    },
  });

  usePageFocus(() => {
    if (resident) {
      reset({
        allergies: resident?.allergies ?? "",
        allergies_media: resident?.allergies_media ?? [],
      });
    }
  }, [resident, reset]);

  const submitForm = () => {
    return new Promise<boolean>((resolve) => {
      if (isSubmitting) {
        resolve(false);
        return;
      }
      handleSubmit(
        async (data: AddResidentAllergiesForm) => {
          try {
            if (!residentId) {
              resolve(false);
              return;
            }

            const allergiesMedia =
              data.allergies_media?.filter<DocumentPickerAsset>((asset) =>
                isDocumentPickerAsset(asset),
              ) || [];
            const allergiesDeletedMedia = resident?.allergies_media?.filter(
              (media) =>
                !data.allergies_media?.some(
                  (item) => isMediaModel(item) && item.id === media.id,
                ),
            );

            if (allergiesDeletedMedia?.length) {
              await Promise.all(
                allergiesDeletedMedia.map((media) =>
                  triggerMediaDelete({
                    residentId,
                    mediaId: media.id,
                  }).unwrap(),
                ),
              );
            }

            const formData = new FormData();
            formData.append("allergies", data.allergies);
            allergiesMedia.forEach((asset) => {
              if (asset.file) formData.append("allergies_media[]", asset.file);
            });

            await triggerUpdateResidentAllergies({
              residentId,
              body: formData,
            }).unwrap();

            showNewToast({
              title: "Allergies Updated",
              message: "Successfully updated resident's allergies.",
              type: "success",
            });
            resolve(true);
          } catch (e) {
            const result = handleBackendError(e, setError, setErrorMessage);
            showErrorToast(e);
            resolve(result);
          }
        },
        (errors) => {
          console.error("Form errors:", errors);
          resolve(false);
        },
      )();
    });
  };
  return {
    control,
    submitForm,
    reset,
    errorMessage,
  };
};
